import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Search, AlertTriangle, Clock, CheckCircle2, CalendarClock, CalendarDays,
} from 'lucide-react';
import {
  Card, Button, Input, Select, Badge, EmptyState, Modal, Label, Textarea, StatusDropdown,
  SkeletonListRows,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { listDeadlines, createDeadline, updateDeadline, listMatters } from '../lib/db';
import { DEADLINE_RULE_TEMPLATES } from '../lib/constants';
import { formatDate } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

const DEADLINE_STATUSES = [
  { value: 'open',      label: 'Open',      tone: 'blue'  },
  { value: 'completed', label: 'Completed', tone: 'green' },
  { value: 'missed',    label: 'Missed',    tone: 'red'   },
  { value: 'waived',    label: 'Waived',    tone: 'slate' },
];

// ─── helpers ────────────────────────────────────────────────────────────────

function addCalendarDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addBusinessDays(date, days) {
  let d = new Date(date);
  let added = 0;
  const step = days < 0 ? -1 : 1;
  while (added < Math.abs(days)) {
    d.setDate(d.getDate() + step);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return d;
}

function calcDueDate(triggerDate, offsetDays, businessDays) {
  if (!triggerDate) return null;
  const base = new Date(triggerDate + 'T12:00:00'); // noon local to avoid DST shift
  const result = businessDays
    ? addBusinessDays(base, offsetDays)
    : addCalendarDays(base, offsetDays);
  return result.toISOString().slice(0, 10);
}

function daysUntil(dueDateStr) {
  if (!dueDateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + 'T00:00:00');
  return Math.round((due - today) / 86400000);
}

function urgencyTone(days) {
  if (days === null) return 'slate';
  if (days < 0)  return 'red';
  if (days <= 7) return 'red';
  if (days <= 14) return 'amber';
  if (days <= 30) return 'blue';
  return 'green';
}

function urgencyLabel(days) {
  if (days === null) return '—';
  if (days < 0)  return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  return `${days}d left`;
}

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high',     label: 'High' },
  { value: 'medium',   label: 'Medium' },
  { value: 'low',      label: 'Low' },
];

const STATUS_OPTIONS = [
  { value: 'open',      label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'waived',    label: 'Waived' },
];

const blank = {
  title: '',
  description: '',
  matter_id: '',
  due_date: '',
  priority: 'high',
  status: 'open',
  rule_template: '',
  trigger_date: '',
};

// ─── component ───────────────────────────────────────────────────────────────

export default function DeadlinesPage() {
  const { firmId, refreshSignal } = useFirm();
  const log = useActivityLogger();
  const [deadlines, setDeadlines] = useState([]);
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving] = useState(false);

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    // Fetch independently so a deadlines error never blocks the matters dropdown
    listDeadlines(firmId, false).then(({ data }) => setDeadlines(data || [])).finally(() => setLoading(false));
    listMatters(firmId).then(({ data }) => setMatters(data || []));
  }, [firmId, refreshSignal]);

  // When user picks a rule template + trigger date, auto-fill due date & title
  // eslint-disable-next-line no-unused-vars
  const applyTemplate = (templateValue, triggerDate) => {
    const tpl = DEADLINE_RULE_TEMPLATES.find((t) => t.value === templateValue);
    if (!tpl) return;
    const due = calcDueDate(triggerDate || form.trigger_date, tpl.offsetDays, tpl.businessDays);
    setForm((f) => ({
      ...f,
      rule_template: templateValue,
      due_date: due || f.due_date,
      title: f.title || tpl.label,
    }));
  };

  const setField = (key, value) => {
    setForm((f) => {
      const updated = { ...f, [key]: value };
      // Re-calculate due date live whenever trigger_date or rule_template changes
      if ((key === 'trigger_date' || key === 'rule_template') && updated.rule_template && updated.trigger_date) {
        const tpl = DEADLINE_RULE_TEMPLATES.find((t) => t.value === updated.rule_template);
        if (tpl) {
          updated.due_date = calcDueDate(updated.trigger_date, tpl.offsetDays, tpl.businessDays) || updated.due_date;
        }
      }
      return updated;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.due_date) return;

    const payload = {
      title: form.title,
      description: form.description || null,
      matter_id: form.matter_id || null,
      due_date: form.due_date,
      priority: form.priority,
      status: form.status,
    };

    // Optimistic add — close modal immediately
    const tempId = `temp-${Date.now()}`;
    const tempRecord = { id: tempId, firm_id: firmId, ...payload };
    setDeadlines((prev) => [...prev, tempRecord].sort((a, b) => a.due_date > b.due_date ? 1 : -1));
    setShowNew(false);
    setForm(blank);

    const { data, error } = await createDeadline(firmId, payload);
    if (error) {
      toast.error(error.message);
      setDeadlines((prev) => prev.filter((d) => d.id !== tempId)); // revert
      return;
    }
    // Replace temp with real record from DB
    setDeadlines((prev) => prev.map((d) => d.id === tempId ? data : d));
    log('deadline', data.id, 'created', `Deadline "${data.title}" added`);
  };

  const changeDeadlineStatus = async (deadline, newStatus) => {
    const prev = deadlines;
    setDeadlines((ds) => ds.map((d) => d.id === deadline.id ? { ...d, status: newStatus } : d));
    const { error } = await updateDeadline(deadline.id, { status: newStatus });
    if (error) { toast.error(error.message); setDeadlines(prev); }
  };

  const filtered = useMemo(() => {
    return deadlines
      .filter((d) => !statusFilter || d.status === statusFilter)
      .filter((d) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (
          d.title?.toLowerCase().includes(s) ||
          d.matter?.title?.toLowerCase().includes(s) ||
          d.matter?.matter_number?.toLowerCase().includes(s)
        );
      });
  }, [deadlines, q, statusFilter]);

  // KPIs
  const open = deadlines.filter((d) => d.status === 'open');
  const overdue = open.filter((d) => daysUntil(d.due_date) < 0);
  const thisWeek = open.filter((d) => { const n = daysUntil(d.due_date); return n !== null && n >= 0 && n <= 7; });
  const thisMonth = open.filter((d) => { const n = daysUntil(d.due_date); return n !== null && n > 7 && n <= 30; });
  const critical = open.filter((d) => d.priority === 'critical');

  const matterOptions = matters.map((m) => ({
    value: m.id,
    label: `${m.matter_number ? m.matter_number + ' · ' : ''}${m.title}`,
  }));

  const selectedTemplate = DEADLINE_RULE_TEMPLATES.find((t) => t.value === form.rule_template);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Deadlines & SOL</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {open.length} open · {overdue.length} overdue
          </p>
        </div>
        <Button icon={Plus} onClick={() => setShowNew(true)}>New deadline</Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Overdue',     value: overdue.length,   icon: AlertTriangle, color: 'text-red-600',   bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',    filter: '' },
          { label: 'Due this week', value: thisWeek.length, icon: Clock,        color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800', filter: 'open' },
          { label: 'Due this month', value: thisMonth.length, icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',  filter: 'open' },
          { label: 'Critical',    value: critical.length,  icon: CheckCircle2,  color: 'text-indigo-600',bg: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800', filter: 'open' },
        ].map(({ label, value, icon: Icon, color, bg, filter }) => (
          <button
            key={label}
            onClick={() => setStatusFilter(filter)}
            className={`rounded-2xl border p-4 text-left hover:shadow-md transition w-full ${bg}`}
          >
            <Icon size={20} className={`${color} mb-2`} />
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">{label}</p>
          </button>
        ))}
      </div>

      {/* Table */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search deadlines…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'All statuses' },
              ...STATUS_OPTIONS,
            ]}
          />
        </div>

        {loading ? (
          <SkeletonListRows rows={6} />
        ) : !filtered.length ? (
          deadlines.length === 0 ? (
            <EmptyState
              icon={CalendarClock}
              title="Never miss a court date"
              description="Deadlines are date-driven obligations — court appearances, filing deadlines, statute of limitations. Add one and DocketFlow surfaces it on the dashboard and on the matter's timeline."
              action={<Button icon={Plus} onClick={() => setShowNew(true)}>New deadline</Button>}
            />
          ) : (
            <EmptyState
              icon={CalendarClock}
              title="No deadlines match your filters"
              description={statusFilter === 'open' ? 'No open deadlines — great!' : 'Try clearing the status filter above.'}
              action={<Button icon={Plus} onClick={() => setShowNew(true)}>New deadline</Button>}
            />
          )
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((dl) => {
                const days = daysUntil(dl.due_date);
                const tone = urgencyTone(days);
                const priorityTone = { critical: 'red', high: 'amber', medium: 'blue', low: 'slate' }[dl.priority] || 'slate';
                return (
                  <li key={dl.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{dl.title}</p>
                        {dl.description && <p className="text-xs text-slate-500 truncate">{dl.description}</p>}
                        {dl.matter && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            <span className="font-medium">{dl.matter.matter_number}</span>{dl.matter.title ? ` · ${dl.matter.title}` : ''}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <Badge tone={tone}>{urgencyLabel(days)}</Badge>
                          <Badge tone={priorityTone}>{dl.priority}</Badge>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs tabular-nums text-slate-500">{formatDate(dl.due_date)}</p>
                        <div className="mt-1">
                          <StatusDropdown value={dl.status} options={DEADLINE_STATUSES} onChange={(s) => changeDeadlineStatus(dl, s)} />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Deadline</th>
                  <th className="text-left px-4 py-2 font-medium">Case File</th>
                  <th className="text-left px-4 py-2 font-medium">Due date</th>
                  <th className="text-left px-4 py-2 font-medium">Urgency</th>
                  <th className="text-left px-4 py-2 font-medium">Priority</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((dl) => {
                  const days = daysUntil(dl.due_date);
                  const tone = urgencyTone(days);
                  const priorityTone = { critical: 'red', high: 'amber', medium: 'blue', low: 'slate' }[dl.priority] || 'slate';
                  // statusTone now handled by StatusDropdown
                  return (
                    <tr key={dl.id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                      <td className="px-4 py-3">
                        <p className="font-medium">{dl.title}</p>
                        {dl.description && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs">{dl.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                        {dl.matter ? (
                          <span>
                            <span className="font-medium">{dl.matter.matter_number}</span>
                            {dl.matter.title && <span className="text-slate-400"> · {dl.matter.title}</span>}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{formatDate(dl.due_date)}</td>
                      <td className="px-4 py-3">
                        <Badge tone={tone}>{urgencyLabel(days)}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={priorityTone}>{dl.priority}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <StatusDropdown
                          value={dl.status}
                          options={DEADLINE_STATUSES}
                          onChange={(s) => changeDeadlineStatus(dl, s)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </Card>

      {/* New deadline modal */}
      <Modal
        open={showNew}
        onClose={() => { setShowNew(false); setForm(blank); }}
        title="New deadline"
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => { setShowNew(false); setForm(blank); }}>Cancel</Button>
            <Button loading={saving} onClick={onSubmit} disabled={!form.title || !form.due_date}>Create deadline</Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* ── Calculator section ── */}
          <div className="rounded-lg border border-indigo-100 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/30 p-4 space-y-3">
            <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-300 uppercase tracking-wide">
              Auto-calculate from rule
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Deadline rule</Label>
                <Select
                  placeholder="Select rule…"
                  options={DEADLINE_RULE_TEMPLATES.map((t) => ({ value: t.value, label: t.label }))}
                  value={form.rule_template}
                  onChange={(e) => setField('rule_template', e.target.value)}
                />
              </div>
              <div>
                <Label>Trigger date</Label>
                <Input
                  type="date"
                  value={form.trigger_date}
                  onChange={(e) => setField('trigger_date', e.target.value)}
                />
              </div>
            </div>
            {selectedTemplate && form.trigger_date && (
              <p className="text-xs text-indigo-600 dark:text-indigo-300">
                <strong>{selectedTemplate.label}</strong>
                {' '}— {selectedTemplate.offsetDays > 0 ? '+' : ''}{selectedTemplate.offsetDays}{' '}
                {selectedTemplate.businessDays ? 'business days' : 'calendar days'}
                {' '}from trigger →{' '}
                <strong>{form.due_date ? formatDate(form.due_date) : '—'}</strong>
                {' '}({selectedTemplate.jurisdiction})
              </p>
            )}
          </div>

          {/* ── Manual fields ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label required>Deadline title</Label>
              <Input
                required
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder="e.g. Answer to Complaint due"
              />
            </div>
            <div>
              <Label required>Due date</Label>
              <Input
                type="date"
                required
                value={form.due_date}
                onChange={(e) => setField('due_date', e.target.value)}
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select
                options={PRIORITY_OPTIONS}
                value={form.priority}
                onChange={(e) => setField('priority', e.target.value)}
              />
            </div>
            <div>
              <Label>Case File</Label>
              <Select
                placeholder="No case file"
                options={matterOptions}
                value={form.matter_id}
                onChange={(e) => setField('matter_id', e.target.value)}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select
                options={STATUS_OPTIONS}
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Additional context…"
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
